import React, { useState, useEffect } from "react";
import { Search, MapPin, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useDebounce } from "@/hooks/useDebounce";

interface InventoryResult {
  product_naam: string;
  eenheid: string;
  locatie: string;
  voorraad: number;
}

interface PackerCorrectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (item: InventoryResult) => void;
  productTitle?: string;
}

const PackerCorrectionDialog = ({ open, onOpenChange, onSelect, productTitle }: PackerCorrectionDialogProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<InventoryResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debouncedSearch = useDebounce(searchTerm, 200);

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    if (debouncedSearch.length < 2) {
      setResults([]);
      return;
    }

    const search = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase.rpc('search_inventory_fast', {
          search_term: debouncedSearch,
          warehouse_filter: 2
        });
        if (error) throw error;
        
        const formatted = (data || []).map((item: any) => ({
          product_naam: String(item.product_naam || '').trim(),
          eenheid: String(item.eenheid || '').trim(),
          locatie: String(item.locatie || '').trim().replace(/\s+/g, ''),
          voorraad: Number(item.voorraad) || 0,
        }));
        setResults(formatted);
      } catch (e) {
        console.error('Correctie zoekfout:', e);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    search();
  }, [debouncedSearch]);

  const handleSelect = (item: InventoryResult) => {
    onSelect(item);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1a1f3d] border-white/10 text-white max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white text-base">Correctie basisverf</DialogTitle>
          {productTitle && (
            <p className="text-white/50 text-xs truncate mt-1">voor: {productTitle}</p>
          )}
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            placeholder="Zoek in voorraad..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30"
            autoFocus
          />
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-1 mt-2">
          {isLoading && (
            <div className="text-center py-8 text-white/40 text-sm">Zoeken...</div>
          )}
          {!isLoading && debouncedSearch.length >= 2 && results.length === 0 && (
            <div className="text-center py-8 text-white/40 text-sm">Geen resultaten</div>
          )}
          {results.map((item, idx) => {
            const isOutOfStock = item.voorraad <= 0;
            return (
              <button
                key={`${item.product_naam}-${item.locatie}-${idx}`}
                onClick={() => handleSelect(item)}
                className={`w-full text-left rounded-lg p-3 transition-colors ${
                  isOutOfStock
                    ? 'bg-red-500/10 border border-red-500/20 hover:bg-red-500/20'
                    : 'bg-white/5 border border-white/10 hover:bg-white/15'
                }`}
              >
                <div className="text-white/90 text-sm font-medium truncate">{item.product_naam}</div>
                <div className="flex items-center gap-3 mt-1 text-xs">
                  {item.eenheid && <span className="text-white/50">{item.eenheid}</span>}
                  <span className="text-white/50 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {item.locatie}
                  </span>
                  <span className={`font-bold ${isOutOfStock ? 'text-red-400' : 'text-emerald-400'}`}>
                    {item.voorraad}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PackerCorrectionDialog;
